import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GroupsIcon from '@mui/icons-material/Groups';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { SmartFilter } from '../../components/common/SmartFilter';
import { ListTable } from '../../components/molecules/ListTable';
import Pagination from '../../components/atoms/Pagination';
import { ImageUpload } from '../../components/atoms/UploadImage';
import withComponentAuth from '../../hoc/withComponentAuth';
import useSnackbar from '../../hooks/useSnackbar';
import { debounce, formatDate } from '../../utils/helper';
import {
  createChannelPartner,
  CreateChannelPartnerPayload,
  getChannelPartnerStats,
  getChannelPartners,
  PartnerStats,
  PartnerStatus,
  PartnerSummary,
} from '../../services/channelPartners';
import {
  BUSINESS_TYPE_OPTIONS,
  PARTNER_TYPE_OPTIONS,
  partnerTypeLabel,
  BusinessFormState,
  AddressFormState,
  BankFormState,
  emptyBusinessForm,
  emptyAddressForm,
  emptyBankForm,
  validateBusinessForm,
  validateAddressForm,
  validateBankForm,
  businessFormToPayload,
  addressFormToPayload,
  bankFormToPayload,
  isValidInPhoneNumber,
} from './partnerFormFields';
import { usePaginationParams } from '../../hooks/usePaginationParams';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';

const ACCENT = '#E31E25';
const ACCENT_HOVER = '#C62828';

const emptyPartnerForm: CreateChannelPartnerPayload = {
  fullName: '',
  phoneNumber: '',
  email: '',
  dateOfBirth: '',
  partnerType: '',
  profilePhoto: '',
};

const sanitizeInMobileDigits = (raw: string) => raw.replace(/\D/g, '').slice(0, 10);

const statuses: PartnerStatus[] = ['DRAFT', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'SUSPENDED'];

const statusMeta: Record<PartnerStatus, { label: string; color: string; bg: string }> = {
  DRAFT: { label: 'Draft', color: '#94a3b8', bg: '#64748b22' },
  UNDER_REVIEW: { label: 'Under review', color: '#f59e0b', bg: '#f59e0b22' },
  APPROVED: { label: 'Approved', color: '#10b981', bg: '#10b98122' },
  REJECTED: { label: 'Rejected', color: '#ef4444', bg: '#ef444422' },
  SUSPENDED: { label: 'Suspended', color: '#a855f7', bg: '#a855f722' },
};

const pretty = (value?: string | null) =>
  value ? value.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'N/A';

const StatusChip = ({ status }: { status: PartnerStatus }) => {
  const meta = statusMeta[status] || statusMeta.DRAFT;
  return <Chip label={meta.label} size="small" sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600 }} />;
};

const StatCard = ({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}) => (
  <Paper sx={{ p: 2, minWidth: 150, flex: 1, bgcolor: '#171717', border: '1px solid #242424', borderRadius: 2 }}>
    <Box display="flex" alignItems="center" gap={1.5}>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: `${color}20`,
          color,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={700} color="#fff" lineHeight={1.1}>
          {value}
        </Typography>
        <Typography variant="caption" color="#64748b">
          {label}
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const ChannelPartners = () => {
  const navigate = useNavigate();
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<CreateChannelPartnerPayload>(emptyPartnerForm);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [phoneDigits, setPhoneDigits] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [creating, setCreating] = useState(false);
  const [includeMoreDetails, setIncludeMoreDetails] = useState(false);
  const [businessForm, setBusinessForm] = useState<BusinessFormState>(emptyBusinessForm);
  const [addressForm, setAddressForm] = useState<AddressFormState>(emptyAddressForm);
  const [bankForm, setBankForm] = useState<BankFormState>(emptyBankForm);

  const [partners, setPartners] = useState<PartnerSummary[]>([]);
  const [stats, setStats] = useState<PartnerStats | null>(null);
  const [loading, setLoading] = useState(false);
  const scrollRef = useScrollRestoration<HTMLDivElement>(!loading);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PartnerStatus | ''>('');
  const [partnerType, setPartnerType] = useState('');
  const { page, rowsPerPage, setPage, setRowsPerPage } = usePaginationParams();
  const [total, setTotal] = useState(0);

  const fetchRequestId = React.useRef(0);

  const fetchData = useCallback(async () => {
    const requestId = ++fetchRequestId.current;
    try {
      setLoading(true);
      const [list, nextStats] = await Promise.all([
        getChannelPartners({ page, limit: rowsPerPage, search, status: statusFilter, partnerType }),
        getChannelPartnerStats(),
      ]);
      if (requestId !== fetchRequestId.current) return;
      setPartners(list.data || []);
      setTotal(list.pagination?.total || 0);
      setStats(nextStats);
    } catch (error) {
      console.error('Failed to load channel partners:', error);
    } finally {
      if (requestId === fetchRequestId.current) setLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter, partnerType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const partnerTypes = useMemo(() => {
    const types = partners.map((p) => p.partnerType).filter(Boolean) as string[];
    return Array.from(new Set(types));
  }, [partners]);

  const handleSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
        setPage(1);
      }, 500),
    [],
  );

  const setField = (field: keyof CreateChannelPartnerPayload, value: string) =>
    setAddForm((prev) => ({ ...prev, [field]: value }));

  const handlePhoneChange = (value: string) => {
    const digits = sanitizeInMobileDigits(value);
    setPhoneDigits(digits);
    setField('phoneNumber', digits);
  };

  const openAdd = () => {
    setAddForm(emptyPartnerForm);
    setPhoneDigits('');
    setPhoneTouched(false);
    setIncludeMoreDetails(false);
    setBusinessForm(emptyBusinessForm);
    setAddressForm(emptyAddressForm);
    setBankForm(emptyBankForm);
    setAddOpen(true);
  };

  const phoneInvalid = phoneTouched && phoneDigits.length > 0 && !isValidInPhoneNumber(phoneDigits);

  const handleCreate = async () => {
    setPhoneTouched(true);
    if (
      !addForm.fullName.trim() ||
      !addForm.email.trim() ||
      !addForm.dateOfBirth ||
      !isValidInPhoneNumber(phoneDigits)
    ) {
      showSnackbar('Name, a valid 10-digit mobile number, email and date of birth are required', 'error');
      return;
    }

    let business: CreateChannelPartnerPayload['business'];
    let address: CreateChannelPartnerPayload['address'];
    let bank: CreateChannelPartnerPayload['bank'];
    if (includeMoreDetails) {
      const businessHasInput = businessForm.businessType || businessForm.legalName.trim();
      const addressHasInput = addressForm.pincode.trim() || addressForm.line1.trim() || addressForm.city.trim();
      const bankHasInput = bankForm.accountHolderName.trim() || bankForm.ifscCode.trim() || bankForm.accountNumber.trim();
      if ((addressHasInput || bankHasInput) && !businessHasInput) {
        showSnackbar('Business details are required before address or bank details', 'error');
        return;
      }
      if (businessHasInput) {
        const businessError = validateBusinessForm(businessForm);
        if (businessError) return showSnackbar(businessError, 'error');
        business = businessFormToPayload(businessForm);
      }
      if (addressHasInput) {
        const addressError = validateAddressForm(addressForm);
        if (addressError) return showSnackbar(addressError, 'error');
        address = addressFormToPayload(addressForm);
      }
      if (bankHasInput) {
        const bankError = validateBankForm(bankForm);
        if (bankError) return showSnackbar(bankError, 'error');
        bank = bankFormToPayload(bankForm);
      }
    }

    try {
      setCreating(true);
      await createChannelPartner({
        ...addForm,
        partnerType: addForm.partnerType || undefined,
        business,
        address,
        bank,
      });
      showSnackbar('Channel partner created', 'success');
      setAddOpen(false);
      setPage(1);
      fetchData();
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Failed to create partner', 'error');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {SnackbarComponent}
      <SmartFilter
        title="Channel Partners"
        createFilter={['SEARCH']}
        onSearch={handleSearch}
        extraActions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{
              backgroundColor: ACCENT,
              '&:hover': { backgroundColor: ACCENT_HOVER },
              textTransform: 'none',
              borderRadius: '10px',
            }}
          >
            Add Partner
          </Button>
        }
      />
      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
        <Box display="flex" gap={1.5} flexWrap="wrap">
          <StatCard label="Total" value={stats?.total || 0} icon={<GroupsIcon fontSize="small" />} color="#3b82f6" />
          <StatCard
            label="Under Review"
            value={stats?.byStatus?.UNDER_REVIEW || 0}
            icon={<HourglassTopIcon fontSize="small" />}
            color="#f59e0b"
          />
          <StatCard
            label="Approved"
            value={stats?.byStatus?.APPROVED || 0}
            icon={<CheckCircleOutlineIcon fontSize="small" />}
            color="#10b981"
          />
          <StatCard
            label="Suspended"
            value={stats?.byStatus?.SUSPENDED || 0}
            icon={<BlockIcon fontSize="small" />}
            color="#a855f7"
          />
        </Box>

        <Paper sx={{ p: 1.5, bgcolor: '#171717', border: '1px solid #242424', borderRadius: 2 }}>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip
              label="All"
              clickable
              onClick={() => {
                setStatusFilter('');
                setPage(1);
              }}
              sx={{ bgcolor: statusFilter === '' ? ACCENT : '#242424', color: '#fff' }}
            />
            {statuses.map((item) => (
              <Chip
                key={item}
                label={statusMeta[item].label}
                clickable
                onClick={() => {
                  setStatusFilter(item);
                  setPage(1);
                }}
                sx={{
                  bgcolor: statusFilter === item ? statusMeta[item].bg : '#242424',
                  color: statusFilter === item ? statusMeta[item].color : '#94a3b8',
                }}
              />
            ))}
            {partnerTypes.map((type) => (
              <Chip
                key={type}
                label={partnerTypeLabel(type)}
                variant="outlined"
                clickable
                onClick={() => {
                  setPartnerType(partnerType === type ? '' : type);
                  setPage(1);
                }}
                sx={{
                  borderColor: partnerType === type ? ACCENT : '#333',
                  color: partnerType === type ? '#fff' : '#94a3b8',
                }}
              />
            ))}
          </Box>
        </Paper>

        <Box
          ref={scrollRef}
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ flex: '1 1 auto', minHeight: 0 }}>
            <ListTable
              headerCells={[
                ['Name', 150],
                ['Phone', 110],
                ['Type', 110],
                ['Status', 110],
                ['Step', 130],
                ['Submitted', 110],
                ['Action', 60],
              ]}
              rows={partners}
              isLoading={loading}
              noDataText="No channel partners found"
              actions={[
                {
                  icon: <VisibilityIcon fontSize="small" />,
                  onClick: (row: PartnerSummary) => navigate(`/channel-partners/${row.id}`),
                },
              ]}
              renderCell={(partner: PartnerSummary, colIndex: number) => {
                switch (colIndex) {
                  case 0:
                    return (
                      <Box>
                        <Typography variant="body2" sx={{ color: '#fff' }}>
                          {partner.fullName || 'Unnamed partner'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#8A8D91' }}>
                          {partner.email || 'No email'}
                        </Typography>
                      </Box>
                    );
                  case 1:
                    return partner.phoneNumber;
                  case 2:
                    return partnerTypeLabel(partner.partnerType);
                  case 3:
                    return <StatusChip status={partner.derivedStatus || partner.status} />;
                  case 4:
                    return pretty(partner.onboardingStep);
                  case 5:
                    return partner.submitted ? formatDate(partner.updatedAt) : 'Not submitted';
                  default:
                    return null;
                }
              }}
            />
          </Box>
          <Pagination
            count={total}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage + 1)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
            }}
          />
        </Box>
      </Box>

      <Dialog
        open={addOpen}
        onClose={() => !creating && setAddOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { bgcolor: '#171717', color: '#fff', border: '1px solid #242424', borderRadius: 3, backgroundImage: 'none' },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700 }}>
          Add Channel Partner
          <IconButton onClick={() => setAddOpen(false)} sx={{ color: '#94a3b8' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ borderColor: '#242424' }} />
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
            <Box sx={{ maxWidth: 160 }}>
              <ImageUpload
                imageUrl={addForm.profilePhoto || ''}
                onChange={(url) => setField('profilePhoto', url)}
                onRemove={() => setField('profilePhoto', '')}
                setLoader={setPhotoUploading}
                uploadLabel="Profile photo"
                fileId="partner-profile-photo-create"
                type="icon"
                folderType={16}
                disabled={photoUploading}
              />
            </Box>
            <TextField label="Full name" value={addForm.fullName} onChange={(e) => setField('fullName', e.target.value)} fullWidth />
            <TextField
              label="Mobile number"
              value={phoneDigits}
              onChange={(e) => handlePhoneChange(e.target.value)}
              onBlur={() => setPhoneTouched(true)}
              placeholder="9812345678"
              error={phoneInvalid}
              helperText={
                phoneInvalid
                  ? 'Enter a valid 10-digit mobile number (starts with 6-9)'
                  : 'Indian mobile only — country code is fixed to +91'
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ color: '#94a3b8', fontWeight: 600, pr: 0.5, borderRight: '1px solid #333' }}>
                      +91
                    </Typography>
                  </InputAdornment>
                ),
              }}
              inputProps={{ maxLength: 10, inputMode: 'numeric' }}
              fullWidth
            />
            <TextField label="Email" type="email" value={addForm.email} onChange={(e) => setField('email', e.target.value)} fullWidth />
            <TextField
              label="Date of birth"
              type="date"
              value={addForm.dateOfBirth}
              onChange={(e) => setField('dateOfBirth', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField select label="Partner type (optional)" value={addForm.partnerType || ''} onChange={(e) => setField('partnerType', e.target.value)} fullWidth>
              <MenuItem value="">None</MenuItem>
              {PARTNER_TYPE_OPTIONS.map((t) => (
                <MenuItem key={t} value={t}>
                  {partnerTypeLabel(t)}
                </MenuItem>
              ))}
            </TextField>

            <Button onClick={() => setIncludeMoreDetails((v) => !v)} sx={{ alignSelf: 'flex-start', color: ACCENT, textTransform: 'none' }}>
              {includeMoreDetails ? '− Hide business, address & bank details' : '+ Add business, address & bank details'}
            </Button>

            <Collapse in={includeMoreDetails}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Divider sx={{ borderColor: '#242424' }}>
                  <Typography variant="caption" color="#64748b">Business</Typography>
                </Divider>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <TextField
                      select
                      fullWidth
                      label="Business type"
                      value={businessForm.businessType}
                      onChange={(e) => setBusinessForm((p) => ({ ...p, businessType: e.target.value as BusinessFormState['businessType'] }))}
                    >
                      <MenuItem value="">None</MenuItem>
                      {BUSINESS_TYPE_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label="Legal Name" value={businessForm.legalName} onChange={(e) => setBusinessForm((p) => ({ ...p, legalName: e.target.value }))} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label="Trading name" value={businessForm.tradingName} onChange={(e) => setBusinessForm((p) => ({ ...p, tradingName: e.target.value }))} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label="GSTIN (optional)" value={businessForm.gstin} onChange={(e) => setBusinessForm((p) => ({ ...p, gstin: e.target.value }))} />
                  </Grid>
                  {businessForm.businessType === 'LIMITED_COMPANY' && (
                    <Grid item xs={6}>
                      <TextField fullWidth label="CIN" value={businessForm.cin} onChange={(e) => setBusinessForm((p) => ({ ...p, cin: e.target.value }))} />
                    </Grid>
                  )}
                  {businessForm.businessType === 'SOLE_TRADER' && (
                    <Grid item xs={6}>
                      <TextField fullWidth label="PAN" value={businessForm.pan} onChange={(e) => setBusinessForm((p) => ({ ...p, pan: e.target.value }))} />
                    </Grid>
                  )}
                </Grid>

                <Divider sx={{ borderColor: '#242424' }}>
                  <Typography variant="caption" color="#64748b">Address</Typography>
                </Divider>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Line 1" value={addressForm.line1} onChange={(e) => setAddressForm((p) => ({ ...p, line1: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Line 2" value={addressForm.line2} onChange={(e) => setAddressForm((p) => ({ ...p, line2: e.target.value }))} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label="City" value={addressForm.city} onChange={(e) => setAddressForm((p) => ({ ...p, city: e.target.value }))} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label="State" value={addressForm.state} onChange={(e) => setAddressForm((p) => ({ ...p, state: e.target.value }))} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label="PIN Code" value={addressForm.pincode} onChange={(e) => setAddressForm((p) => ({ ...p, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))} />
                  </Grid>
                </Grid>

                <Divider sx={{ borderColor: '#242424' }}>
                  <Typography variant="caption" color="#64748b">Bank</Typography>
                </Divider>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Account holder" value={bankForm.accountHolderName} onChange={(e) => setBankForm((p) => ({ ...p, accountHolderName: e.target.value }))} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label="IFSC Code" value={bankForm.ifscCode} onChange={(e) => setBankForm((p) => ({ ...p, ifscCode: e.target.value.toUpperCase() }))} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label="Account No." value={bankForm.accountNumber} onChange={(e) => setBankForm((p) => ({ ...p, accountNumber: e.target.value.replace(/\D/g, '').slice(0, 18) }))} />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Box>
        </DialogContent>
        <Divider sx={{ borderColor: '#242424' }} />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddOpen(false)} disabled={creating} sx={{ color: '#94a3b8', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating}
            sx={{ backgroundColor: ACCENT, '&:hover': { backgroundColor: ACCENT_HOVER }, textTransform: 'none', borderRadius: '10px' }}
          >
            {creating ? 'Creating…' : 'Create Partner'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default withComponentAuth(ChannelPartners);
